import { Constants } from "appConstants";
import {
	BackofficeFilterForm,
	FilterForm,
	FilterFormFooter,
	FilterFormHeader
} from "components/BackofficeFilterForm";
import RadioInput from "components/RadioInput";
import MultiSelect from "components/SelectForm/MultiSelect";
import { isAfter, isBefore } from "date-fns";
import { useFormik } from "formik";
import { DateTimeHelper, ErrorHelper } from "helpers";
import OptionHelper from "helpers/OptionHelper";
import { useCurrentUser } from "providers/CurrentUserProvider";
import { useCallback, useEffect, useMemo } from "react";
import { ApplicationService } from "services/applicationService";
import { ApplicationFilterOptions } from "services/types";
import { authoritiesIncludeGroup } from "utils/roles";
import * as Yup from "yup";

export interface ApplicationListFilters extends Record<string, unknown> {
	statuses: string[];
	interventionTypes: string[];
	concessionaires: string[];
	responsibleGroups: string[];
	analysts: string[];
	projectProgressStatus: string;
	regulatoryAgencies: string[];
}

function validateFutureDate(date: string | undefined) {
	if (date == null) return true;
	return !isAfter(date, DateTimeHelper.currentDate());
}

function validateDatesOrder(from: string | undefined, to: string | undefined) {
	if (to == null || from == null) return true;
	return !isBefore(to, from);
}

const applicationFiltersValidationSchema = Yup.object().shape({
	from: Yup.string().test(
		"valid-from-date",
		"A data inicial é inválida",
		function validateFromDate(from) {
			return validateFutureDate(from);
		}
	),
	to: Yup.string().test(
		"valid-to-date",
		"A data final é inválida",
		function validateToDate(to) {
			return validateFutureDate(to) && validateDatesOrder(this.parent.from, to);
		}
	),
	statuses: Yup.array().of(Yup.string())
});

type BackofficeApplicationListFilterFormProps = Readonly<{
	initialFilters: ApplicationListFilters;
	setFilters: (newFilters: ApplicationListFilters) => void;
	clearFilters: () => void;
	isOpen: boolean;
	setIsOpen: (newIsOpen: boolean) => void;
	filterOptions: ApplicationFilterOptions | null;
	setFilterOptions: (options: ApplicationFilterOptions) => void;
	isLoadingFilterOptions: boolean;
	setIsLoadingFilterOptions: (isLoading: boolean) => void;
	applicationService: ApplicationService;
}>;

export default function BackofficeApplicationListFilterForm({
	initialFilters,
	setFilters,
	clearFilters,
	isOpen,
	setIsOpen,
	filterOptions,
	setFilterOptions,
	isLoadingFilterOptions,
	setIsLoadingFilterOptions,
	applicationService
}: BackofficeApplicationListFilterFormProps): JSX.Element {
	const user = useCurrentUser();

	const formik = useFormik({
		initialValues: initialFilters,
		validationSchema: applicationFiltersValidationSchema,
		enableReinitialize: true,
		onSubmit: (newFilters) => {
			setFilters(newFilters);
			setIsOpen(false);
		}
	});

	const loadOptions = useCallback(() => {
		setIsLoadingFilterOptions(true);
		applicationService
			.getApplicationFilterOptions()
			.then((responseData) => setFilterOptions(responseData))
			.catch((err) => ErrorHelper.showErrorMessage(err))
			.finally(() => setIsLoadingFilterOptions(false));
	}, [applicationService, setFilterOptions, setIsLoadingFilterOptions]);

	useEffect(() => {
		if (filterOptions == null && !isLoadingFilterOptions) loadOptions();
	}, []);

	const { values, handleChange, dirty, errors, handleSubmit, setFieldValue } =
		formik;
	const hasErrors = !!Object.keys(errors).length;

	const shouldViewAgenciesFilter = useMemo(() => {
		return (
			user?.authorities &&
			authoritiesIncludeGroup(
				user?.authorities,
				Constants.UserRoleGroups.ENGENHARIA
			)
		);
	}, [user]);

	return (
		<BackofficeFilterForm
			isOpen={isOpen}
			onClickOutside={() => setIsOpen(false)}
		>
			<FilterFormHeader onClose={() => setIsOpen(false)} title="Filtros" />
			<FilterForm
				onSubmit={handleSubmit}
				isLoading={!filterOptions || isLoadingFilterOptions}
			>
				{filterOptions && (
					<>
						<MultiSelect
							label="Status"
							name="statuses"
							value={values.statuses}
							options={filterOptions.statuses.map(OptionHelper.stringToOption)}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						{shouldViewAgenciesFilter && (
							<MultiSelect
								label="Agência"
								name="regulatoryAgencies"
								value={values.regulatoryAgencies}
								options={filterOptions.regulatoryAgencies}
								isDisabled={false}
								setFieldValue={setFieldValue}
							/>
						)}

						<MultiSelect
							label="Concessionárias"
							name="concessionaires"
							value={values.concessionaires}
							options={filterOptions.concessionaires}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						{filterOptions.groups.length > 1 && (
							<MultiSelect
								label="Grupos responsáveis"
								name="responsibleGroups"
								value={values.responsibleGroups}
								options={filterOptions.groups}
								isDisabled={false}
								setFieldValue={setFieldValue}
							/>
						)}

						<MultiSelect
							label="Tipos de intervenção"
							name="interventionTypes"
							value={values.interventionTypes}
							options={filterOptions.interventionTypes.map(
								OptionHelper.stringToOption
							)}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						<MultiSelect
							label="Analistas"
							name="analysts"
							value={values.analysts}
							options={filterOptions.analysts}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						<RadioInput
							label="Etapa do processo"
							name="projectProgressStatus"
							options={[
								{ label: "Funcional", value: "FUNCIONAL" },
								{ label: "Executivo", value: "EXECUTIVO" },
								{ label: "Não Classificado", value: "LEGADO" }
							]}
							value={values.projectProgressStatus}
							onChange={(evt) => {
								handleChange(evt);
							}}
						/>
					</>
				)}
			</FilterForm>
			<FilterFormFooter
				dirty={dirty}
				hasErrors={hasErrors}
				onClear={() => {
					formik.resetForm();
					clearFilters();
				}}
				onSubmit={formik.submitForm}
			/>
		</BackofficeFilterForm>
	);
}
